import TextField from "@/components/TextField";
import { LitMenu, LitMenuItem, MdMenu } from "@/components/Menu/LitMenu";
import { useRef, useState, useEffect } from "react";
import { useIsInsideModal } from "@/hooks/useModalContext";

type MenuDropdownProps<T> = {
  label: string;
  options: T[];
  value?: T;
  onSelect(value: T): void;
  getLabel(option: T): string;
  renderItem(option: T): React.ReactNode;
  disabled?: boolean;
  constrainMenuWidth?: boolean;
  positioning?: "absolute" | "popover" | "fixed";
  noVerticalFlip?: boolean;
};

export default function MenuDropdown<T>({
  options,
  value,
  onSelect,
  label,
  getLabel,
  renderItem,
  disabled = false,
  constrainMenuWidth = false,
  positioning: positioningProp,
  noVerticalFlip: noVerticalFlipProp
}: MenuDropdownProps<T>) {
  const insideModal = useIsInsideModal();
  const positioning = positioningProp ?? (insideModal ? "fixed" : "absolute");
  const noVerticalFlip = noVerticalFlipProp ?? !insideModal;

  const [showOptions, setShowOptions] = useState(false);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<MdMenu>(null);

  const inputWidth = textFieldRef.current?.getBoundingClientRect()?.width;
  const menuElement = menuRef.current?.shadowRoot?.querySelector(".menu") as MdMenu | undefined;

  useEffect(() => {
    if (menuElement && inputWidth && constrainMenuWidth) {
      menuElement.style.maxWidth = `${inputWidth}px`;
      menuElement.style.width = `${inputWidth}px`;
    }
  }, [menuElement, inputWidth, showOptions, constrainMenuWidth]);

  return (
    <div className="relative">
      <div
        className="w-full"
        onClick={() => {
          if (!disabled && options.length > 0) {
            setShowOptions((prev) => !prev);
          }
        }}
      >
        <TextField
          label={label}
          className="w-full"
          ref={textFieldRef}
          value={value && getLabel(value)}
          readOnly
          trailingIcon="arrow_drop_down"
          disabled={disabled}
        />
      </div>

      <LitMenu
        positioning={positioning}
        noVerticalFlip={noVerticalFlip}
        anchorElement={textFieldRef.current}
        open={showOptions}
        onClosed={() => {
          setShowOptions(false);
        }}
        ref={menuRef}
        className="max-h-[300px] overflow-auto"
      >
        {options.map((option, i) => (
          <LitMenuItem
            key={i}
            onClick={() => {
              onSelect(option);
              setShowOptions(false);
            }}
            style={{ minWidth: inputWidth }}
            className="border-b border-sys-brand-surface-container-high last:border-b-0 hover:bg-sys-brand-surface-container max-w-full"
          >
            <div className="py-1 break-words">{renderItem(option)}</div>
          </LitMenuItem>
        ))}
      </LitMenu>
    </div>
  );
}
